import React, { useState } from 'react';

import {
  Box,
  Checkbox,
  HStack,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';

import { Button } from '@/components/core/Button';

import {
  FEEDBACK_REASONS,
  FEEDBACK_TEXTAREA_MAX_LENGTH,
} from '../../constants';

export interface FeedbackContentProps {
  initialFocusRef?: React.RefObject<HTMLTextAreaElement | null>;
  onAccept: (payload: {
    feedback: string;
    reasons: string[];
  }) => void | Promise<void>;
  onCancel?: () => void;
}

export const FeedbackContent: React.FC<FeedbackContentProps> = ({
  onAccept,
  initialFocusRef,
}) => {
  const [feedback, setFeedback] = useState('');
  const [submittedFeedback, setSubmittedFeedback] = useState<string | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reasons, setReasons] = useState<string[]>([]);

  const toggleReason = (reason: string) => {
    setReasons(prev =>
      prev.includes(reason) ? prev.filter(r => r !== reason) : [...prev, reason]
    );
  };

  const handleSubmit = () => {
    const value = feedback.trim();
    const payload = {
      feedback: value,
      reasons,
    };
    setIsSubmitting(true);
    Promise.resolve(onAccept(payload))
      .then(() => setSubmittedFeedback(value))
      .finally(() => setIsSubmitting(false));
  };
  if (submittedFeedback !== null) {
    return (
      <VStack align='flex-start' gap={0} p={4}>
        <Text size='headingPrimary'>Feedback submitted!</Text>
        <Text mt={1} size='secondaryRegular' opacity={0.6}>
          Feedback recorded. Thank you for your input.
        </Text>
        {submittedFeedback.trim() !== '' && (
          <Box
            mt={4}
            mb={1}
            width='100%'
            py={2}
            px={6}
            borderLeftWidth='2px'
            borderLeftColor='accent.500'
            fontStyle='italic'
          >
            <Text size='secondaryRegular'>{submittedFeedback}</Text>
          </Box>
        )}
      </VStack>
    );
  }

  return (
    <VStack gap={0} align='flex-start'>
      <VStack
        gap={0}
        align='flex-start'
        width='100%'
        p={4}
        borderBottom='1px solid'
        borderBottomColor='white.100'
      >
        <Text size='secondaryRegular'>Additional feedback</Text>
        <Textarea
          ref={initialFocusRef}
          my={1.5}
          py={1}
          px={0}
          value={feedback}
          resize='none'
          fontSize='sm'
          onChange={e =>
            setFeedback(e.target.value.slice(0, FEEDBACK_TEXTAREA_MAX_LENGTH))
          }
          minH='68px'
          width='100%'
          maxLength={FEEDBACK_TEXTAREA_MAX_LENGTH}
          variant='unstyled'
          borderBottom='1px solid'
          borderBottomColor='whiteAlpha.400'
          borderRadius={0}
          bg='transparent'
          _focus={{
            boxShadow: 'none',
            outline: 'none',
            borderBottomColor: 'white',
          }}
          _focusVisible={{
            boxShadow: 'none',
            outline: 'none',
            borderBottomColor: 'white',
          }}
        />
        <Text size='secondaryRegular' opacity={0.5}>
          {feedback.length} / {FEEDBACK_TEXTAREA_MAX_LENGTH} characters
        </Text>

        <VStack align='flex-start' gap={2} mt={3}>
          {FEEDBACK_REASONS.map(({ value, label }) => (
            <Checkbox
              key={value}
              sx={{
                '& [data-part=label], & .chakra-checkbox__label': {
                  fontSize: 'sm',
                  marginLeft: 2,
                },
              }}
              isChecked={reasons.includes(value)}
              onChange={() => toggleReason(value)}
            >
              {label}
            </Checkbox>
          ))}
        </VStack>
      </VStack>

      <HStack width='100%' justify='flex-end' p={4}>
        <Button
          onClick={handleSubmit}
          isLoading={isSubmitting}
          disabled={feedback.trim().length === 0 && reasons.length === 0}
          variant='glassy'
        >
          Submit feedback
        </Button>
      </HStack>
    </VStack>
  );
};
